import { __rest, __assign, __awaiter, __generator } from '../../node_modules/tslib/tslib.es6.js';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import { styled, useTheme, useMediaQuery, Grid } from '@mui/material';
import React, { useState, useRef, useMemo } from 'react';
import { AnalyticsTrackingEvent } from '../../analytics/events.js';
import brandclub_colors from '../../colors/brandclubColors.js';
import { AppButton } from '../../Components/AppButton/index.js';
import { WIDGET_SPACE, TABLET_SCREEN_SIZE, WIDGET_SPACE_HALF, TABLET_HORIZONTAL_PADDING, MOBILE_SCREEN_SIZE, MOBILE_HORIZONTAL_PADDING, DESKTOP_SCREEN_SIZE, DESKTOP_HORIZONTAL_PADDING } from '../../utils/constants/Constants.js';
import { useOverflow } from '../../utils/hooks/useOverflow.js';
import 'lodash/isEqual';
import 'lodash/get';
import 'lodash/orderBy';
import { buildRewardList } from '../../utils/productUtils.js';
import { decodeHTMLCharacters, getRetailerFromId } from '../../utils/stringUtils.js';
import { sendTextCallback } from '../../utils/textMessage.js';
import { BrandclubJoinClub } from '../components/BrandclubJoinClub/index.js';
import { BCPhoneInputVariant } from '../components/BrandclubPhoneInput/index.js';
import { GenericContainer } from '../components/GenericContainer.js';
import { PopupDrawer } from '../components/PopupDrawer/PopupDrawer.js';
import { RecommendedProducts } from '../RecommendedProducts/index.js';
import { ProductRewardRow } from './ProductRewardRow.js';

var _a, _b, _c;
var FEATURES_PREVIEW_LINES = 12;
var HeaderContainer = styled(GenericContainer)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: 50,
            paddingBottom: WIDGET_SPACE,
            paddingLeft: '5%',
            paddingRight: '5%',
            boxSizing: 'border-box',
            color: theme.palette.primary.main
        },
        _b["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
            backgroundColor: '#fff',
            padding: "".concat(WIDGET_SPACE_HALF, "px ").concat(TABLET_HORIZONTAL_PADDING, "px"),
        },
        _b["@media screen and (max-width: ".concat(MOBILE_SCREEN_SIZE, "px)")] = {
            backgroundColor: '#fff',
            padding: "".concat(WIDGET_SPACE_HALF, "px 0px 0px"),
        },
        _b);
});
var Container = styled('div')((_a = {
        maxWidth: DESKTOP_SCREEN_SIZE - DESKTOP_HORIZONTAL_PADDING * 2,
        fontSize: 16,
        width: '100%',
        img: {
            borderRadius: 15,
            maxWidth: '100%',
        },
        '.summary-title': {
            fontSize: 20,
            fontWeight: 600,
            paddingBottom: 3,
        }
    },
    _a["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
        img: {
            borderRadius: 5,
        },
    },
    _a['.grid-container'] = (_b = {
            display: 'grid',
            gridTemplateColumns: 'auto 483px',
            alignItems: 'flex-start',
            gap: 52
        },
        _b["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
            gap: 0,
            gridTemplateColumns: '1fr',
        },
        _b["@media screen and (max-width: ".concat(MOBILE_SCREEN_SIZE, "px)")] = {
            '.features-container': {
                marginLeft: MOBILE_HORIZONTAL_PADDING,
                marginRight: MOBILE_HORIZONTAL_PADDING,
            },
        },
        _b['.features'] = {
            margin: '20px 0 0',
            display: '-webkit-box',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: FEATURES_PREVIEW_LINES,
            '.feature': {
                marginBottom: 24, // must match text line height so that overflown text is not abruptly truncated
            },
        },
        _b['.show-more-container'] = {
            margin: '20px 0',
            '.show-more': {
                border: 'none',
                padding: 0,
                textDecoration: 'underline',
                ':hover': {
                    backgroundColor: 'transparent',
                },
            },
        },
        _b),
    _a['.rewards-section'] = (_c = {
            border: "1px solid ".concat(brandclub_colors.blueWhiteBorder),
            borderRadius: 8,
            padding: '0px !important',
            overflow: 'hidden'
        },
        _c["@media screen and (max-width: ".concat(MOBILE_SCREEN_SIZE, "px)")] = {
            border: 'none',
            borderRadius: 0,
        },
        _c),
    _a));
var DialogContent = styled('div')({
    '.summary-title': {
        fontSize: 20,
        fontWeight: 600,
        paddingBottom: 6,
    },
    '.features': {
        margin: '20px 0 0',
        '.feature': {
            marginBottom: 24, // must match text line height so that overflown text is not abruptly truncated
        },
    },
});
var BulletPoints = function (_a) {
    var _b, _c, _d, _e;
    var bulletPoints = _a.bulletPoints, brandEntity = _a.brandEntity, mainEntity = _a.mainEntity, axios = _a.axios, analyticsTrack = _a.analyticsTrack, trackingParams = _a.trackingParams, appConfig = _a.appConfig, brandclub = _a.brandclub, useNavigate = _a.useNavigate, userProfile = _a.userProfile, customerSpendByBrand = _a.customerSpendByBrand, onShareClick = _a.onShareClick, handleJoinClub = _a.handleJoinClub, onAddToCart = _a.onAddToCart, props = __rest(_a, ["bulletPoints", "brandEntity", "mainEntity", "axios", "analyticsTrack", "trackingParams", "appConfig", "brandclub", "useNavigate", "userProfile", "customerSpendByBrand", "onShareClick", "handleJoinClub", "onAddToCart"]);
    var theme = useTheme();
    var _f = useState(false), popupOpen = _f[0], setPopupOpen = _f[1];
    var overflowContainerRef = useRef(null);
    var isOverflow = useOverflow({ containerRef: overflowContainerRef });
    var desktopView = useMediaQuery("(min-width:".concat(TABLET_SCREEN_SIZE, "px)"));
    var tabletView = useMediaQuery("(max-width:".concat(TABLET_SCREEN_SIZE, "px)"));
    // needs to be memoized for reference equality used in useRecommendedProducts
    var recommendedProductsVariable = useMemo(function () {
        var _a, _b, _c;
        var searchImprovementTreatment = (props.pageContext.abTestingTreatments || {}).searchImprovementTreatment;
        var treatment = searchImprovementTreatment ? { search: searchImprovementTreatment } : undefined;
        return {
            stacklineSku: (_a = mainEntity === null || mainEntity === void 0 ? void 0 : mainEntity.entity) === null || _a === void 0 ? void 0 : _a.stacklineSku,
            brandId: (_b = mainEntity === null || mainEntity === void 0 ? void 0 : mainEntity.entity) === null || _b === void 0 ? void 0 : _b.brandId,
            subCategoryId: (_c = mainEntity === null || mainEntity === void 0 ? void 0 : mainEntity.entity) === null || _c === void 0 ? void 0 : _c.subCategoryId,
            isInternalCompany: !!props.isInternalCompany,
            treatment: treatment,
        };
    }, [props.pageContext.sku, props.pageContext.brandId, props.pageContext.subCategoryId]);
    var bulletPointsToUse = useMemo(function () {
        var _a, _b;
        var bps = [];
        for (var i = 0; i < bulletPoints.length; i++) {
            // read from entity if propValue is empty string
            if (bulletPoints[i]) {
                bps.push(bulletPoints[i]);
            }
            else if ((_b = (_a = mainEntity === null || mainEntity === void 0 ? void 0 : mainEntity.entity) === null || _a === void 0 ? void 0 : _a.featureBullets) === null || _b === void 0 ? void 0 : _b[i]) {
                bps.push(mainEntity.entity.featureBullets[i]);
            }
        }
        return bps;
    }, [bulletPoints, (_b = mainEntity === null || mainEntity === void 0 ? void 0 : mainEntity.entity) === null || _b === void 0 ? void 0 : _b.bulletPoint]);
    var rewardList = buildRewardList(((_c = mainEntity === null || mainEntity === void 0 ? void 0 : mainEntity.entity) === null || _c === void 0 ? void 0 : _c.campaignRewards) || [], (_d = brandEntity === null || brandEntity === void 0 ? void 0 : brandEntity.brandclub) !== null && _d !== void 0 ? _d : brandclub, customerSpendByBrand);
    var brandName = decodeHTMLCharacters(((_e = brandEntity === null || brandEntity === void 0 ? void 0 : brandEntity.entity) === null || _e === void 0 ? void 0 : _e.name) || '');
    var handleShowMoreClick = function () { return setPopupOpen(true); };
    var handlePopupClose = function () { return setPopupOpen(false); };
    var handleSendText = function (phoneNumber) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, sendTextCallback(phoneNumber, axios, brandEntity, analyticsTrack, trackingParams, AnalyticsTrackingEvent.WEB_APP_SOCIAL_LINK_TEXT_APP_DOWNLOAD_LINK)];
        });
    }); };
    return (React.createElement(React.Fragment, null,
        React.createElement(HeaderContainer, null,
            React.createElement(Container, null,
                React.createElement(Grid, { container: true, spacing: 1, className: "grid-container" },
                    React.createElement(Grid, { item: true, className: "features-container" },
                        React.createElement("div", { className: "summary-title" }, "Product summary"),
                        React.createElement("div", null, "Key features"),
                        React.createElement("div", { className: "features", ref: overflowContainerRef }, bulletPointsToUse.map(function (bulletPoint, index) {
                            return bulletPoint ? (React.createElement(React.Fragment, { key: index },
                                React.createElement("div", { className: "feature" }, bulletPoint))) : null;
                        })),
                        isOverflow && (React.createElement("div", { className: "show-more-container", onClick: handleShowMoreClick },
                            React.createElement(AppButton, { className: "show-more", style: __assign({ color: theme.palette.primary.main, fontFamily: 'poppins', fontWeight: 600 }, (tabletView ? { fontSize: 16 } : {})) },
                                "Show more",
                                React.createElement(KeyboardArrowRightRoundedIcon, { sx: { height: '21px' } }))))),
                    React.createElement(Grid, { item: true, className: "rewards-section", style: rewardList.length > 0 ? {} : { opacity: 0 } }, rewardList.length > 0 ? (React.createElement(React.Fragment, null,
                        React.createElement(Grid, { item: true, padding: desktopView ? '32px 36px' : '32px 28px' },
                            React.createElement("div", { className: "summary-title" }, "Rewards offered"),
                            React.createElement("div", null, brandName ? "".concat(brandName, " eligible rewards") : 'Eligible rewards'),
                            React.createElement(Grid, { container: true, display: "flex", flexDirection: "column", gap: "25px", paddingTop: '30px' }, rewardList.map(function (_a, index) {
                                var _b, _c;
                                var id = _a.id, amount = _a.amount, subTitle = _a.subTitle;
                                return (React.createElement(ProductRewardRow, __assign({ key: index }, {
                                    rewardType: id,
                                    rewardAmount: amount,
                                    retailer: {
                                        name: getRetailerFromId((_b = mainEntity === null || mainEntity === void 0 ? void 0 : mainEntity.entity) === null || _b === void 0 ? void 0 : _b.retailerId),
                                        retailerProductPageUrl: ((_c = mainEntity === null || mainEntity === void 0 ? void 0 : mainEntity.entity) === null || _c === void 0 ? void 0 : _c.retailerProductPageUrl) || '#',
                                    },
                                    trackingParams: trackingParams,
                                    analyticsTrack: analyticsTrack,
                                    mainEntity: mainEntity,
                                    brandEntity: brandEntity,
                                    domainConfig: appConfig === null || appConfig === void 0 ? void 0 : appConfig.domainConfig,
                                    subTitle: subTitle,
                                    useNavigate: useNavigate,
                                    onShareClick: onShareClick,
                                    handleJoinClub: handleJoinClub,
                                    onAddToCart: onAddToCart,
                                })));
                            }))),
                        userProfile ? null : (React.createElement(Grid, { item: true },
                            React.createElement(BrandclubJoinClub, { userProfile: userProfile, title: "Get the app", subtitle: "Download Brandclub to access rewards", containerStyle: desktopView ? { padding: '30px 36px' } : { padding: '30px 20px' }, titleStyle: { fontSize: '20px', fontWeight: 'bold' }, subtitleStyle: { fontSize: '16px', fontWeight: 500 }, inputVariant: BCPhoneInputVariant.SM, brandclubPhoneInputProps: {
                                    buttonText: 'Send',
                                }, handleSendText: handleSendText }))))) : null)),
                React.createElement(RecommendedProducts, { apolloClient: props.apolloClient, product: recommendedProductsVariable, linkComponent: props.LinkComponent, isUserSignedIn: !!userProfile }))),
        React.createElement(PopupDrawer, { open: popupOpen, onClose: handlePopupClose, dialogProps: { PaperProps: { style: { maxWidth: desktopView ? '700px' : '100vw' } } } },
            React.createElement(DialogContent, { sx: { padding: '0 35px 35px' } },
                React.createElement("div", { className: "summary-title" }, "Product summary"),
                React.createElement("div", null, "Key features"),
                React.createElement("div", { className: "features" }, bulletPointsToUse.map(function (bulletPoint, index) {
                    return bulletPoint ? (React.createElement(React.Fragment, { key: index },
                        React.createElement("div", { className: "feature" }, bulletPoint))) : null;
                }))))));
};

export { BulletPoints };
//# sourceMappingURL=index.js.map
